import { toastr } from 'react-redux-toastr';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { api } from '~/services/api';
import { ApplicationState } from '~/store';
import { CategoriaProps, ResponseProps } from '../../../utils/helper';
import {
  areasFailure,
  areasRequest,
  areasSuccess,
  deleteTratamentoAtividadesFailure,
  deleteTratamentoAtividadesRequest,
  deleteTratamentoAtividadesSuccess,
  postTratamentoAtividadesFailure,
  postTratamentoAtividadesRequest,
  postTratamentoAtividadesSuccess,
  tratamentoAtividadesFailure,
  tratamentoAtividadesRequest,
  tratamentoAtividadesSuccess,
} from './actions';
import { AtividadesType } from './types';

function* getArea({ payload }: ReturnType<typeof areasRequest>) {
  const { token, company } = yield select(state => state.auth);

  try {
    const response: ResponseProps = yield call(api.get, '/v1/areas', {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        company: company?.country_company_id,
      },
    });

    yield put(areasSuccess(response.data));
  } catch (error) {
    yield put(areasFailure());
  }
}

function* tratamentoAtividades({
  payload,
}: ReturnType<typeof tratamentoAtividadesRequest>) {
  const { token, company } = yield select(state => state.auth);

  try {
    const response: ResponseProps = yield call(
      api.get,
      `/v1/atividades?pageNo=${payload.newPage}&pageSize=${payload.rowsPerPage}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          company: company?.country_company_id,
        },
      },
    );

    const categorias: CategoriaProps = yield call(api.get, `/v1/categories`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: '',
        company: company?.country_company_id,
      },
    });

    const tratamentoAtividades: any = [];

    response.data.forEach((element: any) => {
      tratamentoAtividades.push({
        empresa: element.country_company_id,
        codigoDaAtividade: element.data_operation_id,
        area: element.company_area_id,
        atividadeDeTratamentoDeDados: element.data_treat_operation_nm,
        pessoaResponsavel: element.responsable_nm,
        contatoDaPessoaResponsavel: element.responsable_contact_ds,
        tipoDoTitularDeDados: element.atividade_titular.map((el: any) => {
          const index = categorias.data.findIndex(
            (cat: any) => cat.data_holder_cat_id === el.data_holder_cat_id,
          );

          return index !== -1
            ? `${categorias.data[index].data_holder_cat_name}, `
            : '';
        }),
        tipoDoConsentimento: element.consent_type_fl,
        situacao: element.status_fl,
        user_executor: element.user_executor,
        acao: undefined,
        total_itens: element.total_itens,
      });
    });

    const data = {
      tratamentoAtividades,
      categorias: categorias.data,
    };

    yield put(tratamentoAtividadesSuccess(data));
  } catch (error) {
    yield put(tratamentoAtividadesFailure());
  }
}

function* postTraTamentoAtividades({
  payload,
}: ReturnType<typeof postTratamentoAtividadesRequest>) {
  const profile = yield select((state: ApplicationState) => state.user.profile);

  try {
    const {
      area,
      atividadeDeTratamentoDeDados,
      contatoDaPessoaResponsavel,
      empresa,
      pessoaResponsavel,
      situacao,
      tipoConsentimento,
      tipoDoTitularDeDados,
    } = payload.row;

    const categorias: CategoriaProps = yield call(api.get, `/v1/categories`, {
      headers: {
        'Content-Type': 'application/json',
        company: empresa,
      },
    });

    const update = [
      {
        company_area_id: area,
        data_treat_operation_nm: atividadeDeTratamentoDeDados,
        consent_type_fl: tipoConsentimento == 1 ? 'Y' : 'N',
        status_fl: situacao == 1 ? 'Y' : 'N',
        country_company_id: empresa,
        responsable_nm: pessoaResponsavel,
        responsable_contact_ds: contatoDaPessoaResponsavel,
        atividade_titular: tipoDoTitularDeDados
          .map((el: any) => el.replace(',', '').trim())
          .map((el: any) => {
            const index = categorias.data.findIndex(
              (cat: any) => cat.data_holder_cat_name === el,
            );
            if (index !== -1) {
              return {
                country_company_id: categorias.data[index].country_company_id,
                data_holder_cat_id: categorias.data[index].data_holder_cat_id,
                status_fl: categorias.data[index].status_fl,
              };
            }
          }),
        user_executor: profile?.sub,
      },
    ];

    yield call(api.post, '/v1/atividades', update, {
      headers: {
        'Content-Type': 'application/json',
        // Authorization: `Bearer ${token}`,
        company: empresa,
      },
    });

    yield put(postTratamentoAtividadesSuccess());

    yield put(tratamentoAtividadesRequest(0, 5));

    const toastrConfirmOptions = {
      onOk: () => {},
      onCancel: () => {},
      okText: 'Ok',
      disableCancel: true,
    };

    toastr.confirm(
      'Inclusão de atividade efetuada com sucesso.',
      toastrConfirmOptions,
    );
  } catch (error) {
    const toastrConfirmOptions = {
      onOk: () => {},
      onCancel: () => {},
      okText: 'Ok',
      disableCancel: true,
    };

    toastr.confirm(
      'Problemas no acesso ao servidor. Por favor, tente mais tarde.',
      toastrConfirmOptions,
    );

    yield put(postTratamentoAtividadesFailure());
  }
}

function* updateTraTamentoAtividades({
  payload,
}: ReturnType<typeof postTratamentoAtividadesRequest>) {
  const profile = yield select((state: ApplicationState) => state.user.profile);

  const { token } = yield select(state => state.auth);

  try {
    const {
      area,
      atividadeDeTratamentoDeDados,
      codigoDaAtividade,
      contatoDaPessoaResponsavel,
      empresa,
      pessoaResponsavel,
      situacao,
      tipoConsentimento,
      tipoDoTitularDeDados,
    } = payload.row;

    const categorias: CategoriaProps = yield call(api.get, `/v1/categories`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: '',
        company: empresa,
      },
    });

    const update = [
      {
        company_area_id: area,
        data_operation_id: codigoDaAtividade,
        data_treat_operation_nm: atividadeDeTratamentoDeDados,
        consent_type_fl: tipoConsentimento == 1 ? 'Y' : 'N',
        status_fl: situacao === 1 ? 'Y' : 'N',
        country_company_id: empresa,
        responsable_nm: pessoaResponsavel,
        responsable_contact_ds: contatoDaPessoaResponsavel,
        atividade_titular: tipoDoTitularDeDados
          .map((el: any) => el.replace(',', '').trim())
          .map((el: any) => {
            const index = categorias.data.findIndex(
              (cat: any) => cat.data_holder_cat_name === el,
            );
            if (index !== -1) {
              return {
                country_company_id: categorias.data[index].country_company_id,
                data_holder_cat_id: categorias.data[index].data_holder_cat_id,
                data_operation_id: codigoDaAtividade,
                status_fl: categorias.data[index].status_fl,
              };
            }
          }),
        user_executor: profile?.sub,
      },
    ];

    yield call(api.post, '/v1/atividades', update, {
      headers: {
        'Content-Type': 'application/json',
        // Authorization: `Bearer ${token}`,
        company: empresa,
      },
    });

    yield put(postTratamentoAtividadesSuccess());

    yield put(tratamentoAtividadesRequest(0, 5));

    const toastrConfirmOptions = {
      onOk: () => {},
      onCancel: () => {},
      okText: 'Ok',
      disableCancel: true,
    };

    toastr.confirm(
      'Alteração de atividade efetuada com sucesso.',
      toastrConfirmOptions,
    );
  } catch (error) {
    const toastrConfirmOptions = {
      onOk: () => {},
      onCancel: () => {},
      okText: 'Ok',
      disableCancel: true,
    };

    toastr.confirm(
      'Problemas no acesso ao servidor. Por favor, tente mais tarde.',
      toastrConfirmOptions,
    );

    yield put(postTratamentoAtividadesFailure());
  }
}

function* deleteTraTamentoAtividades({
  payload,
}: ReturnType<typeof deleteTratamentoAtividadesRequest>) {
  try {
    const { codigoDaAtividade, empresa } = payload.row;

    const update: any = {
      data_operation_id: codigoDaAtividade,
      country_company_id: empresa,
    };

    yield call(api.delete, '/v1/atividades', {
      data: update,
      headers: {
        'Content-Type': 'application/json',
        company: empresa,
      },
    });

    yield put(deleteTratamentoAtividadesSuccess());

    yield put(tratamentoAtividadesRequest(0, 5));

    const toastrConfirmOptions = {
      onOk: () => {},
      onCancel: () => {},
      okText: 'Ok',
      disableCancel: true,
    };

    toastr.confirm('Atividade deletada com sucesso.', toastrConfirmOptions);
  } catch (error) {
    const toastrConfirmOptions = {
      onOk: () => {},
      onCancel: () => {},
      okText: 'Ok',
      disableCancel: true,
    };

    toastr.confirm(
      'Problemas no acesso ao servidor. Por favor, tente mais tarde.',
      toastrConfirmOptions,
    );

    yield put(deleteTratamentoAtividadesFailure());
  }
}

export default all([
  takeLatest(AtividadesType.AREA_RESQUEST, getArea),
  takeLatest(
    AtividadesType.MANTER_ATIVIDADE_TRATAMENTO_DADOS_RESQUEST,
    tratamentoAtividades,
  ),
  takeLatest(
    AtividadesType.POST_MANTER_ATIVIDADE_TRATAMENTO_DADOS_RESQUEST,
    postTraTamentoAtividades,
  ),
  takeLatest(
    AtividadesType.UPDATE_MANTER_ATIVIDADE_TRATAMENTO_DADOS_RESQUEST,
    updateTraTamentoAtividades,
  ),
  takeLatest(
    AtividadesType.DELETE_MANTER_ATIVIDADE_TRATAMENTO_DADOS_RESQUEST,
    deleteTraTamentoAtividades,
  ),
]);
